import { Status, statusColors, StatusColors } from '@nexelem/components';
import { useDateFormatter } from '@nexelem/utils';
import { memo, ReactNode } from 'react';
import { NodeBase, NodeBaseProps, ProductionOrderNumber, NodeLabel } from '../NodeBase';
import { calculateProgressPercentage, getContentFromValues } from '../../utils';
import { useCustomTranslation } from '../../../../../../hooks';
import { MultipleTableAvatar } from '../../../../../molecules/MultipleTableAvatar';
import { ResourceView, TaskStatus } from '@nexelem/graphne-api';
import clsx from 'clsx';
import { GanttUtils } from '../../../utils';
import { GanttShowOnTaskOptions } from '../../../components/SettingsDrawer';
import { formatUnit } from '../../../Kanban/components/KanbanTask';
import { ShadowParams } from '../NodeBase/StyledNodeBase';
import { useIsGanttSimulation } from '../../../GanttSimulation/hooks/GanttSimulationContext';
import { NodeFlags } from '../NodeFlags';
import { NodeFlags as NodeFlagsType } from '../../mappers';

export type NodeSingleProps = Omit<
  NodeBaseProps,
  'children' | 'unassigned' | 'id' | 'isRetooling' | 'isOverdue' | 'hasComment'
> & {
  width: number;
  title: string;
  date: string;
  id: number;
  statusColor: StatusColors;
  showOnTask: GanttShowOnTaskOptions;
  children?: ReactNode;
  current?: number;
  total: number;
  unit?: string;
  taskExecutionId?: number | string;
  salesOrderIds?: string[];
  clients?: string[];
  productionOrderNumber?: string;
  productionOrderExternalNumber?: string;
  dueDate?: string;
  mode?: 'default' | 'worker';
  softResourceAssignments?: ResourceView[];
  requiredSoftResourcesAmount?: number;
  status?: TaskStatus;
  shadowParams?: ShadowParams;
  nodeFlags: NodeFlagsType;
};

export const NodeSingleBase = memo(
  ({
    statusColor,
    total,
    isDisabled,
    width,
    isDraggable,
    isDragging,
    children,
    isSmall,
    title,
    retoolingWidth,
    hasError,
    date,
    showOnTask,
    unit = '',
    current = 0,
    salesOrderIds,
    clients,
    productionOrderNumber,
    productionOrderExternalNumber,
    dueDate,
    mode = 'default',
    softResourceAssignments,
    requiredSoftResourcesAmount,
    status,
    id,
    nodeFlags,
    ref,
    ...props
  }: NodeSingleProps) => {
    const { tWrapper } = useCustomTranslation('products.productionPlan.gantt.node');
    const dateFormatter = useDateFormatter({});
    const isSimulation = useIsGanttSimulation();

    const salesOrderIdsContent = getContentFromValues(salesOrderIds);
    const clientsContent = getContentFromValues(clients);
    const isWorkerView = mode === 'worker';

    const showDueDate = showOnTask.dueDate && dueDate;
    const showSalesOrderId = showOnTask.salesOrderId && salesOrderIdsContent;
    const showClient = showOnTask.client && clientsContent;

    const { avatars, unassigned, hasAssignedWorkers } = GanttUtils.getGanttUnassigned({
      showOnTask,
      mode,
      softResourceAssignments,
      requiredSoftResourcesAmount,
    });

    const shouldShowFullProgress = status !== TaskStatus.Pending || current > 0;
    const formattedCurrent = formatUnit(current);
    const formattedTotal = formatUnit(total);
    const progressWithUnit = shouldShowFullProgress
      ? `${calculateProgressPercentage(current, total)}% ${formattedCurrent}/${formattedTotal} ${unit}`
      : `${formattedTotal} ${unit}`;

    const showStatus = isSimulation
      ? !((nodeFlags.frozen || !nodeFlags.toPublish) && status === TaskStatus.Pending)
      : true;

    return (
      <NodeBase
        hasError={hasError}
        isDisabled={isDisabled}
        isDraggable={isDraggable}
        isDragging={isDragging}
        isOverdue={nodeFlags.overdue}
        isRetooling={nodeFlags.retooling}
        isSmall={isSmall}
        ref={ref}
        retoolingWidth={retoolingWidth}
        toPublish={nodeFlags.toPublish}
        unassigned={unassigned}
        warehouseShortage={nodeFlags.warehouseShortage}
        width={width}
        {...props}
      >
        {children}
        <div className={clsx('relative z-99 flex h-full max-w-full items-center')} style={{ width: `${width}px` }}>
          {showStatus && <Status className={clsx('mr-1 first-of-type:ml-1')} color={statusColor} variant="dot" />}
          <NodeFlags className="first-of-type:ml-1" flags={nodeFlags} nodeId={id} />

          <div className={clsx('ml-1 flex h-full flex-col justify-center gap-0.5 overflow-hidden')}>
            {!isSmall && (
              <div className="flex w-full min-w-0 items-center gap-1">
                <span className="min-w-5 shrink truncate text-s11-sb text-text-primary uppercase">{title}</span>

                {/* Right Elements Container: Must allow internal shrinking */}
                <div className="flex min-w-0 shrink-2 items-center gap-1 empty:hidden">
                  {showSalesOrderId && <NodeLabel className="min-w-0 shrink-2">{salesOrderIdsContent}</NodeLabel>}

                  {showClient && <NodeLabel className="min-w-0 shrink-3">{clientsContent}</NodeLabel>}

                  {/* Rightmost Element: Highest shrink priority (Truncates first) */}
                  <ProductionOrderNumber
                    productionOrderExternalNumber={productionOrderExternalNumber}
                    productionOrderNumber={productionOrderNumber}
                  />
                </div>
              </div>
            )}

            {!isDisabled && (
              <div className="flex gap-2 truncate empty:hidden">
                {unassigned && (
                  <span className="min-w-5 truncate text-s11-sb text-text-status-orange uppercase">
                    {tWrapper('assign')}
                  </span>
                )}
                {showOnTask.progress && (
                  <span
                    className={clsx(
                      'text-s11-sb text-nowrap',
                      nodeFlags.overdue && !hasError
                        ? 'text-text-status-rose-darker'
                        : statusColors({ text: statusColor }),
                    )}
                  >
                    {progressWithUnit}
                  </span>
                )}
                {showOnTask.time && <span className="text-s11 text-text-secondary-darker">{date}</span>}
                {showDueDate && (
                  <span className="text-s11-sb text-text-state-neutral-darker">
                    {`${tWrapper('dueDate')} ${dateFormatter(dueDate)}`}
                  </span>
                )}
              </div>
            )}
          </div>
          {(isWorkerView || hasAssignedWorkers) && <MultipleTableAvatar avatars={avatars} className="ml-auto pl-1" />}
        </div>
      </NodeBase>
    );
  },
);

NodeSingleBase.displayName = 'NodeSingleBase';
